import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Bot, Database, Cloud, Shield, Coins, Save, Filter as FilterIcon, X, Percent, Building2, Plus, Wrench, Split } from "lucide-react";
import { toast } from "sonner";

export default function SettingsPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "coordenacao";
  const [settings, setSettings] = useState(null);
  const [proLabore, setProLabore] = useState("");
  const [proLaboreTaxPct, setProLaboreTaxPct] = useState("");
  const [bvsmTaxPct, setBvsmTaxPct] = useState("");
  const [bvsmRetentionPct, setBvsmRetentionPct] = useState("");
  const [anesthSharePct, setAnesthSharePct] = useState("");
  const [savingDist, setSavingDist] = useState(false);
  const [apexTiers, setApexTiers] = useState([]);
  const [techCost, setTechCost] = useState({ manha: "", tarde: "", noite: "", dia_fds: "", noite_fds: "" });
  const [savingTaxes, setSavingTaxes] = useState(false);
  const [savingApex, setSavingApex] = useState(false);
  const [savingTech, setSavingTech] = useState(false);
  const [filterSpecs, setFilterSpecs] = useState([]);
  const [newSpec, setNewSpec] = useState("");
  const [savingSpecs, setSavingSpecs] = useState(false);
  const [saving, setSaving] = useState(false);
  // Per-hospital tech cost overrides (Rorainópolis etc.)
  const [hospitals, setHospitals] = useState([]);
  const [hospitalDrafts, setHospitalDrafts] = useState({});
  const [savingHospital, setSavingHospital] = useState({});

  function _hospitalToDraft(h) {
    return {
      tech_cost_per_day: h.tech_cost_per_day == null ? "" : String(h.tech_cost_per_day),
      tech_cost_manha: h.tech_cost_manha == null ? "" : String(h.tech_cost_manha),
      tech_cost_tarde: h.tech_cost_tarde == null ? "" : String(h.tech_cost_tarde),
      tech_cost_noite: h.tech_cost_noite == null ? "" : String(h.tech_cost_noite),
      tech_cost_dia_fds: h.tech_cost_dia_fds == null ? "" : String(h.tech_cost_dia_fds),
      tech_cost_noite_fds: h.tech_cost_noite_fds == null ? "" : String(h.tech_cost_noite_fds),
    };
  }

  async function loadHospitals() {
    try {
      const r = await api.get("/hospitals");
      const list = (r.data || []).filter((h) => h.active !== false);
      setHospitals(list);
      const drafts = {};
      list.forEach((h) => { drafts[h.id] = _hospitalToDraft(h); });
      setHospitalDrafts(drafts);
    } catch (e) { /* non-fatal: hospitals card just won't render */ }
  }

  function updateHospitalDraft(hid, field, value) {
    setHospitalDrafts((prev) => ({ ...prev, [hid]: { ...(prev[hid] || {}), [field]: value } }));
  }

  function _numOrNull(s) {
    if (s === "" || s == null) return null;
    const v = Number(s);
    return Number.isFinite(v) ? v : null;
  }

  async function saveHospitalTechCost(hid) {
    const draft = hospitalDrafts[hid] || {};
    const payload = {
      tech_cost_per_day: _numOrNull(draft.tech_cost_per_day),
      tech_cost_manha: _numOrNull(draft.tech_cost_manha),
      tech_cost_tarde: _numOrNull(draft.tech_cost_tarde),
      tech_cost_noite: _numOrNull(draft.tech_cost_noite),
      tech_cost_dia_fds: _numOrNull(draft.tech_cost_dia_fds),
      tech_cost_noite_fds: _numOrNull(draft.tech_cost_noite_fds),
    };
    setSavingHospital((p) => ({ ...p, [hid]: true }));
    try {
      await api.patch(`/hospitals/${hid}`, payload);
      toast.success("Regra do hospital atualizada.");
      loadHospitals();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingHospital((p) => ({ ...p, [hid]: false })); }
  }

  async function load() {
    try {
      const r = await api.get("/settings");
      setSettings(r.data);
      setProLabore(String(r.data.pro_labore_per_shift ?? ""));
      setProLaboreTaxPct(((r.data.pro_labore_tax_rate ?? 0) * 100).toFixed(2));
      setBvsmTaxPct(((r.data.bvsm_estimated_tax_rate ?? 0) * 100).toFixed(2));
      setBvsmRetentionPct(((r.data.bvsm_retention_rate ?? 0.35) * 100).toFixed(2));
      setAnesthSharePct(((r.data.anesthesiologist_share_rate ?? 0.25) * 100).toFixed(2));
      setApexTiers((r.data.apex_commission_tiers || []).map((t) => ({
        up_to: t.up_to == null ? "" : String(t.up_to),
        rate_pct: ((t.rate ?? 0) * 100).toFixed(2),
      })));
      const tc = r.data.tech_cost_per_turno || {};
      setTechCost({
        manha: String(tc.manha ?? ""),
        tarde: String(tc.tarde ?? ""),
        noite: String(tc.noite ?? ""),
        dia_fds: String(tc.dia_fds ?? ""),
        noite_fds: String(tc.noite_fds ?? "0"),
      });
      setFilterSpecs(r.data.filter_specialties || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  useEffect(() => { load(); loadHospitals(); /* eslint-disable-next-line */ }, []);

  async function saveFinance() {
    const v = Number(proLabore);
    if (!Number.isFinite(v) || v < 0) { toast.error("Valor inválido."); return; }
    setSaving(true);
    try {
      await api.patch("/settings", { pro_labore_per_shift: v });
      toast.success("Pro-labore atualizado.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  async function saveTaxes() {
    const pl = Number(proLaboreTaxPct);
    const bv = Number(bvsmTaxPct);
    if (!Number.isFinite(pl) || pl < 0 || pl > 100) {
      toast.error("Imposto sobre pro-labore inválido (0 a 100%)."); return;
    }
    if (!Number.isFinite(bv) || bv < 0 || bv > 100) {
      toast.error("Imposto BVSM inválido (0 a 100%)."); return;
    }
    setSavingTaxes(true);
    try {
      await api.patch("/settings", {
        pro_labore_tax_rate: pl / 100,
        bvsm_estimated_tax_rate: bv / 100,
      });
      toast.success("Impostos atualizados.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingTaxes(false); }
  }

  async function saveDistribution() {
    const ret = Number(bvsmRetentionPct);
    const an = Number(anesthSharePct);
    if (!Number.isFinite(ret) || ret < 0 || ret > 100) {
      toast.error("Retenção BVSM inválida (0 a 100%)."); return;
    }
    if (!Number.isFinite(an) || an < 0 || an > 100) {
      toast.error("Fatia do anestesista inválida (0 a 100%)."); return;
    }
    setSavingDist(true);
    try {
      await api.patch("/settings", {
        bvsm_retention_rate: ret / 100,
        anesthesiologist_share_rate: an / 100,
      });
      toast.success("Regra de distribuição atualizada. Os relatórios financeiros já refletem os novos percentuais.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingDist(false); }
  }

  function updateTier(idx, field, value) {
    setApexTiers((tiers) => tiers.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  }

  function addTier() {
    setApexTiers([...apexTiers, { up_to: "", rate_pct: "0.00" }]);
  }

  function removeTier(idx) {
    setApexTiers((tiers) => tiers.filter((_, i) => i !== idx));
  }

  async function saveApex() {
    const cleaned = [];
    for (let i = 0; i < apexTiers.length; i++) {
      const t = apexTiers[i];
      const rate = Number(t.rate_pct);
      if (!Number.isFinite(rate) || rate < 0 || rate > 100) {
        toast.error(`Faixa ${i + 1}: alíquota inválida.`); return;
      }
      const up = t.up_to === "" || t.up_to == null ? null : Number(t.up_to);
      if (up != null && (!Number.isFinite(up) || up <= 0)) {
        toast.error(`Faixa ${i + 1}: limite "Até" inválido.`); return;
      }
      cleaned.push({ up_to: up, rate: rate / 100 });
    }
    if (cleaned.length === 0) { toast.error("Defina pelo menos uma faixa."); return; }
    if (cleaned[cleaned.length - 1].up_to != null) {
      toast.error('A última faixa deve ter "Até" vazio (= acima de tudo).');
      return;
    }
    setSavingApex(true);
    try {
      await api.patch("/settings", { apex_commission_tiers: cleaned });
      toast.success("Faixas Apex atualizadas.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingApex(false); }
  }

  async function saveTechCost() {
    const cleaned = {};
    for (const k of ["manha", "tarde", "noite", "dia_fds", "noite_fds"]) {
      const v = Number(techCost[k]);
      if (!Number.isFinite(v) || v < 0) {
        toast.error(`Valor inválido em ${k}.`); return;
      }
      cleaned[k] = v;
    }
    setSavingTech(true);
    try {
      await api.patch("/settings", { tech_cost_per_turno: cleaned });
      toast.success("Custo de instrumentadores atualizado.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingTech(false); }
  }

  function addSpec() {
    const v = newSpec.trim();
    if (!v) return;
    if (filterSpecs.some((s) => s.toLowerCase() === v.toLowerCase())) {
      toast.error("Especialidade já adicionada.");
      return;
    }
    setFilterSpecs([...filterSpecs, v]);
    setNewSpec("");
  }

  function removeSpec(s) {
    setFilterSpecs(filterSpecs.filter((x) => x !== s));
  }

  async function saveSpecs() {
    setSavingSpecs(true);
    try {
      await api.patch("/settings", { filter_specialties: filterSpecs });
      toast.success(filterSpecs.length === 0
        ? "Filtro de especialidades desativado (todas passam)."
        : `Filtrando apenas: ${filterSpecs.join(", ")}.`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingSpecs(false); }
  }

  return (
    <div className="space-y-5" data-testid="settings-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Configurações</h1>
        <p className="text-sm text-slate-500 mt-1">Parâmetros financeiros, integrações e status do sistema.</p>
      </div>

      {/* Financial settings */}
      <Card data-testid="card-finance-settings"><CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Coins className="h-5 w-5 text-amber-700" />
          <h3 className="font-display font-semibold">Parâmetros Financeiros</h3>
        </div>
        <p className="text-xs text-slate-500">
          Este pro-labore é descontado do valor líquido por papel para cada turno (manhã, tarde ou noite)
          que a pessoa atuou. O restante é o lucro a distribuir.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="space-y-1.5">
            <Label>Pro-labore por turno (R$)</Label>
            <Input type="number" step="0.01" min="0"
              value={proLabore} onChange={(e) => setProLabore(e.target.value)}
              disabled={!canEdit}
              data-testid="input-pro-labore" />
          </div>
          <div className="md:col-span-2 flex gap-2">
            <Button className="bg-blue-700 hover:bg-blue-800" onClick={saveFinance}
              disabled={!canEdit || saving} data-testid="btn-save-pro-labore">
              <Save className="h-4 w-4 mr-1" /> {saving ? "Salvando..." : "Salvar"}
            </Button>
            {!canEdit && (
              <div className="text-xs text-slate-500 self-center">
                Apenas administradores/coordenação podem alterar.
              </div>
            )}
          </div>
        </div>
        {settings?.updated_at && (
          <div className="text-[11px] text-slate-400">Atualizado em {new Date(settings.updated_at).toLocaleString("pt-BR")}</div>
        )}
      </CardContent></Card>

      {/* Impostos */}
      <Card data-testid="card-taxes-settings"><CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Percent className="h-5 w-5 text-rose-700" />
          <h3 className="font-display font-semibold">Impostos sobre faturamento e pro-labore</h3>
        </div>
        <p className="text-xs text-slate-500">
          <strong>Imposto sobre pro-labore:</strong> deduzido do lucro de cada pessoa após o pro-labore. Default 26,8% (INSS + IRRF estimado).<br />
          <strong>Imposto BVSM estimado:</strong> percentual sobre o faturamento bruto da empresa, exibido em <em>Controle BVSM</em>. Default 7,8%.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="space-y-1.5">
            <Label>Imposto sobre pro-labore (%)</Label>
            <Input type="number" step="0.01" min="0" max="100"
              value={proLaboreTaxPct} onChange={(e) => setProLaboreTaxPct(e.target.value)}
              disabled={!canEdit}
              data-testid="input-pro-labore-tax" />
          </div>
          <div className="space-y-1.5">
            <Label>Imposto BVSM estimado (%)</Label>
            <Input type="number" step="0.01" min="0" max="100"
              value={bvsmTaxPct} onChange={(e) => setBvsmTaxPct(e.target.value)}
              disabled={!canEdit}
              data-testid="input-bvsm-tax" />
          </div>
          <div className="flex gap-2">
            <Button className="bg-blue-700 hover:bg-blue-800" onClick={saveTaxes}
              disabled={!canEdit || savingTaxes} data-testid="btn-save-taxes">
              <Save className="h-4 w-4 mr-1" /> {savingTaxes ? "Salvando..." : "Salvar impostos"}
            </Button>
          </div>
        </div>
      </CardContent></Card>

      {/* Regra de distribuição (NOVO — fev 2026) */}
      <Card data-testid="card-distribution-settings"><CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Split className="h-5 w-5 text-emerald-700" />
          <h3 className="font-display font-semibold">Regra de distribuição do valor bruto</h3>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-xs text-emerald-900 leading-relaxed">
          <strong>Como funciona:</strong> sobre o valor bruto recebido pela BVSM em cada caso (recebido → faturado → aprovado, na ordem de preferência):
          <ol className="list-decimal ml-5 mt-1.5 space-y-0.5">
            <li>BVSM retém <strong>{bvsmRetentionPct}%</strong>.</li>
            <li>Os <strong>{(100 - Number(bvsmRetentionPct || 0)).toFixed(2)}%</strong> restantes são a <em>produção</em>.</li>
            <li>Da produção, <strong>{anesthSharePct}%</strong> vai para o anestesista.</li>
            <li>O resto (<strong>{(100 - Number(anesthSharePct || 0)).toFixed(2)}%</strong> da produção) é dividido em partes <strong>iguais</strong> entre os cirurgiões DAQUELE caso — pode ser <strong>2 (cirurgião + auxiliar)</strong> ou <strong>3 (cirurgião + auxiliar + 3º)</strong>, depende do que cada caso tem cadastrado.</li>
          </ol>
          <p className="mt-2 text-[11px] text-emerald-700">
            ⚠️ A divisão é feita <strong>caso a caso</strong>, automaticamente: se um caso tem 2 cirurgiões, o valor é dividido por 2; se tem 3, é dividido por 3. Você adiciona o 3º cirurgião na tela de detalhes do caso (campo "3º Cirurgião"). Mudanças nos % afetam imediatamente <em>Faturamento</em>, <em>Financeiro</em>, <em>Controle BVSM</em>, <em>Analytics</em> e os exports Excel.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="space-y-1.5">
            <Label>Retenção BVSM (%)</Label>
            <Input type="number" step="0.01" min="0" max="100"
              value={bvsmRetentionPct}
              onChange={(e) => setBvsmRetentionPct(e.target.value)}
              disabled={!canEdit}
              data-testid="input-bvsm-retention" />
          </div>
          <div className="space-y-1.5">
            <Label>Fatia do anestesista (% da produção)</Label>
            <Input type="number" step="0.01" min="0" max="100"
              value={anesthSharePct}
              onChange={(e) => setAnesthSharePct(e.target.value)}
              disabled={!canEdit}
              data-testid="input-anesth-share" />
          </div>
          <div className="flex gap-2">
            <Button
              className="bg-blue-700 hover:bg-blue-800"
              onClick={saveDistribution}
              disabled={!canEdit || savingDist}
              data-testid="btn-save-distribution"
            >
              <Save className="h-4 w-4 mr-1" /> {savingDist ? "Salvando..." : "Salvar regra"}
            </Button>
          </div>
        </div>
        <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
          <strong>Exemplos</strong> com R$ 10.000 de bruto, retenção {bvsmRetentionPct}% e fatia anestesista {anesthSharePct}%:
          {(() => {
            const g = 10000;
            const ret = (Number(bvsmRetentionPct) || 0) / 100;
            const an = (Number(anesthSharePct) || 0) / 100;
            const bvsm = g * ret;
            const pool = g - bvsm;
            const anest = pool * an;
            const surgPot = pool - anest;
            const perSurg2 = surgPot / 2;
            const perSurg3 = surgPot / 3;
            const fmt = (v) => (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
            return (
              <ul className="list-disc ml-5 mt-1 space-y-0.5">
                <li>BVSM fica com {fmt(bvsm)}</li>
                <li>Anestesista recebe {fmt(anest)}</li>
                <li>
                  <strong>Caso com 2 cirurgiões:</strong> cada um leva {fmt(perSurg2)}{" "}
                  <span className="text-slate-400">({fmt(surgPot)} ÷ 2)</span>
                </li>
                <li>
                  <strong>Caso com 3 cirurgiões:</strong> cada um leva {fmt(perSurg3)}{" "}
                  <span className="text-slate-400">({fmt(surgPot)} ÷ 3)</span>
                </li>
              </ul>
            );
          })()}
        </div>
      </CardContent></Card>

      {/* Apex commission tiers */}
      <Card data-testid="card-apex-tiers"><CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-violet-700" />
          <h3 className="font-display font-semibold">Comissão Apex — faixas progressivas</h3>
        </div>
        <p className="text-xs text-slate-500">
          A comissão Apex é calculada sobre o <strong>faturamento bruto MENSAL</strong> da empresa
          (zera todo dia 01). Cada faixa aplica sua alíquota apenas sobre o valor que entra naquela
          janela. Configure uma sequência crescente; a <strong>última faixa</strong> deve ter
          o campo <em>"Até"</em> vazio — significa <em>"acima de tudo"</em>.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-2 py-2">#</th>
                <th className="text-left px-2 py-2">Até (R$)</th>
                <th className="text-left px-2 py-2">Alíquota (%)</th>
                <th className="text-right px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {apexTiers.map((t, idx) => (
                <tr key={idx} className="border-t border-slate-100" data-testid={`apex-tier-row-${idx}`}>
                  <td className="px-2 py-2 text-slate-500">{idx + 1}</td>
                  <td className="px-2 py-2">
                    <Input type="number" step="0.01" min="0"
                      value={t.up_to} onChange={(e) => updateTier(idx, "up_to", e.target.value)}
                      placeholder={idx === apexTiers.length - 1 ? "Vazio = acima de tudo" : "Ex: 500000"}
                      disabled={!canEdit} className="w-48"
                      data-testid={`input-tier-upto-${idx}`} />
                  </td>
                  <td className="px-2 py-2">
                    <Input type="number" step="0.01" min="0" max="100"
                      value={t.rate_pct} onChange={(e) => updateTier(idx, "rate_pct", e.target.value)}
                      disabled={!canEdit} className="w-32"
                      data-testid={`input-tier-rate-${idx}`} />
                  </td>
                  <td className="px-2 py-2 text-right">
                    {canEdit && apexTiers.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeTier(idx)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`btn-remove-tier-${idx}`}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={addTier} data-testid="btn-add-tier">
              <Plus className="h-4 w-4 mr-1" /> Adicionar faixa
            </Button>
            <Button className="bg-blue-700 hover:bg-blue-800 ml-auto" onClick={saveApex}
              disabled={savingApex} data-testid="btn-save-apex">
              <Save className="h-4 w-4 mr-1" /> {savingApex ? "Salvando..." : "Salvar faixas Apex"}
            </Button>
          </div>
        )}
      </CardContent></Card>

      {/* Tech cost per turno */}
      <Card data-testid="card-tech-cost"><CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Wrench className="h-5 w-5 text-cyan-700" />
          <h3 className="font-display font-semibold">Custo de Instrumentadores por turno</h3>
        </div>
        <p className="text-xs text-slate-500">
          Cobrado da BVSM (sai do <em>Resultado BVSM</em> em <em>Controle BVSM</em>) — depois do rateio
          de pro-labore e divisão de lucros entre sócios.
          <br />
          <strong>Apenas turnos com pelo menos 1 cirurgia entram no cálculo.</strong>
          <br />
          Dia útil (seg-sex): manhã/tarde/noite cobrados separadamente.
          <br />
          FDS (sáb/dom): turno único <strong>"Dia"</strong> (cobre manhã + tarde) — se houver cirurgia
          em qualquer um dos dois períodos no mesmo dia, conta 1 turno. Noite no FDS por enquanto fica zerada.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 items-end">
          {[
            { k: "manha",     label: "Manhã (seg-sex)" },
            { k: "tarde",     label: "Tarde (seg-sex)" },
            { k: "noite",     label: "Noite (seg-sex)" },
            { k: "dia_fds",   label: "Dia (sáb/dom) — cobre manhã+tarde" },
            { k: "noite_fds", label: "Noite (sáb/dom) — usual: R$ 0" },
          ].map(({ k, label }) => (
            <div className="space-y-1.5" key={k}>
              <Label className="text-[11px] uppercase tracking-wider text-slate-600">{label}</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">R$</span>
                <Input type="number" step="0.01" min="0"
                  value={techCost[k]}
                  onChange={(e) => setTechCost({ ...techCost, [k]: e.target.value })}
                  disabled={!canEdit}
                  className="pl-9"
                  data-testid={`input-tech-${k}`} />
              </div>
            </div>
          ))}
        </div>
        {canEdit && (
          <div className="flex justify-end">
            <Button className="bg-blue-700 hover:bg-blue-800" onClick={saveTechCost}
              disabled={savingTech} data-testid="btn-save-tech-cost">
              <Save className="h-4 w-4 mr-1" /> {savingTech ? "Salvando..." : "Salvar custo de instrumentadores"}
            </Button>
          </div>
        )}
      </CardContent></Card>

      {/* Per-hospital tech cost overrides */}
      <Card data-testid="card-tech-cost-by-hospital"><CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-cyan-700" />
          <h3 className="font-display font-semibold">Regras por hospital (custo de instrumentador)</h3>
        </div>
        <p className="text-xs text-slate-500">
          Cada hospital pode <strong>sobrescrever</strong> a grade global acima. Deixe um campo
          vazio pra usar o padrão global.
          <br />
          <strong>Modelo "Por diária"</strong>: quando um valor é preenchido aqui (ex.: Rorainópolis = R$ 600),
          o sistema <strong>ignora os 5 campos de turno</strong> e cobra 1× esse valor por <strong>dia</strong> que teve cirurgia
          (independente de manhã/tarde/noite).
        </p>
        {hospitals.length === 0 ? (
          <div className="text-sm text-slate-500 py-4">Nenhum hospital cadastrado. Cadastre em <em>Cadastros → Hospitais</em>.</div>
        ) : (
          <div className="space-y-4">
            {hospitals.map((h) => {
              const draft = hospitalDrafts[h.id] || _hospitalToDraft(h);
              const isPerDay = (draft.tech_cost_per_day || "").trim() !== "" && Number(draft.tech_cost_per_day) > 0;
              return (
                <div key={h.id} className="border border-slate-200 rounded-md p-4 space-y-3" data-testid={`hospital-tech-${h.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <strong className="text-slate-900">{h.short_name || h.name}</strong>
                      <span className="text-xs text-slate-500">{h.municipality || ""}</span>
                      {isPerDay && (
                        <span className="inline-block text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                          Diária
                        </span>
                      )}
                    </div>
                    {canEdit && (
                      <Button size="sm" className="bg-blue-700 hover:bg-blue-800"
                        onClick={() => saveHospitalTechCost(h.id)}
                        disabled={!!savingHospital[h.id]}
                        data-testid={`btn-save-hospital-tech-${h.id}`}>
                        <Save className="h-3.5 w-3.5 mr-1" />
                        {savingHospital[h.id] ? "Salvando..." : "Salvar"}
                      </Button>
                    )}
                  </div>
                  {/* Per-day input — highlighted because it overrides */}
                  <div className={`border rounded-md p-3 ${isPerDay ? "border-emerald-300 bg-emerald-50/50" : "border-slate-200 bg-slate-50"}`}>
                    <Label className="text-[11px] uppercase tracking-wider text-slate-600">
                      Diária (R$ por dia com cirurgia) — sobrescreve grade por turno
                    </Label>
                    <div className="relative mt-1.5 max-w-[240px]">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">R$</span>
                      <Input type="number" step="0.01" min="0"
                        value={draft.tech_cost_per_day}
                        onChange={(e) => updateHospitalDraft(h.id, "tech_cost_per_day", e.target.value)}
                        disabled={!canEdit}
                        className="pl-9"
                        placeholder="vazio = usa modelo por turno"
                        data-testid={`hospital-tech-${h.id}-per-day`} />
                    </div>
                  </div>
                  {/* 5 turnos */}
                  <div className={`grid grid-cols-2 md:grid-cols-5 gap-3 ${isPerDay ? "opacity-50 pointer-events-none" : ""}`}>
                    {[
                      { k: "tech_cost_manha", label: "Manhã" },
                      { k: "tech_cost_tarde", label: "Tarde" },
                      { k: "tech_cost_noite", label: "Noite" },
                      { k: "tech_cost_dia_fds", label: "Dia FDS" },
                      { k: "tech_cost_noite_fds", label: "Noite FDS" },
                    ].map(({ k, label }) => (
                      <div key={k} className="space-y-1.5">
                        <Label className="text-[11px] uppercase tracking-wider text-slate-600">{label}</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">R$</span>
                          <Input type="number" step="0.01" min="0"
                            value={draft[k] || ""}
                            onChange={(e) => updateHospitalDraft(h.id, k, e.target.value)}
                            disabled={!canEdit || isPerDay}
                            className="pl-9"
                            placeholder="global"
                            data-testid={`hospital-tech-${h.id}-${k}`} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent></Card>

      {/* Specialty filter for raw maps */}
      <Card data-testid="card-specialty-filter"><CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <FilterIcon className="h-5 w-5 text-blue-700" />
          <h3 className="font-display font-semibold">Filtro de Especialidade no Mapa</h3>
        </div>
        <p className="text-xs text-slate-500">
          Quando o mapa bruto é filtrado, apenas linhas com <strong>especialidade</strong> listada abaixo são incluídas.
          Deixe vazio para incluir todas as especialidades (comportamento padrão).
          Exemplo: adicione <code className="text-[11px] bg-slate-100 px-1 rounded">C. Geral</code> se sua equipe só realiza cirurgia geral.
        </p>
        <div className="flex flex-wrap gap-2">
          {filterSpecs.length === 0 ? (
            <span className="text-xs text-slate-400 italic">Nenhuma especialidade configurada — todas passam.</span>
          ) : filterSpecs.map((s) => (
            <span key={s} className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-900 rounded-full px-3 py-1 text-xs font-medium" data-testid={`spec-chip-${s}`}>
              {s}
              {canEdit && (
                <button onClick={() => removeSpec(s)} className="hover:text-red-600" title="Remover" data-testid={`btn-remove-spec-${s}`}>
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2 items-end">
            <div className="space-y-1.5 flex-1 min-w-[200px] max-w-sm">
              <Label>Adicionar especialidade</Label>
              <Input
                value={newSpec}
                onChange={(e) => setNewSpec(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSpec(); } }}
                placeholder="Ex: C. Geral"
                data-testid="input-new-spec"
              />
            </div>
            <Button variant="outline" onClick={addSpec} data-testid="btn-add-spec">Adicionar</Button>
            <Button className="bg-blue-700 hover:bg-blue-800" onClick={saveSpecs} disabled={savingSpecs} data-testid="btn-save-specs">
              <Save className="h-4 w-4 mr-1" /> {savingSpecs ? "Salvando..." : "Salvar filtro"}
            </Button>
          </div>
        )}
        <div className="text-[11px] text-slate-400">
          Esta configuração se aplica a <strong>novos filtros</strong> e também ao botão <em>Re-filtrar</em> de mapas existentes.
        </div>
      </CardContent></Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card><CardContent className="p-6 space-y-3">
          <div className="flex items-center gap-2"><Bot className="h-5 w-5 text-blue-700" /><h3 className="font-display font-semibold">Assistente IA (Claude)</h3></div>
          <p className="text-xs text-slate-500">Extração de documentos, detecção de divergências e chat operacional.</p>
          <div className="text-sm px-3 py-2 rounded-md bg-amber-50 text-amber-800 border border-amber-200">
            Chave Anthropic não configurada. Envie a chave para habilitar.
          </div>
        </CardContent></Card>

        <Card><CardContent className="p-6 space-y-3">
          <div className="flex items-center gap-2"><Cloud className="h-5 w-5 text-emerald-700" /><h3 className="font-display font-semibold">Armazenamento (GCS)</h3></div>
          <p className="text-xs text-slate-500">Uploads são armazenados localmente até a configuração do Google Cloud Storage.</p>
          <div className="text-sm px-3 py-2 rounded-md bg-amber-50 text-amber-800 border border-amber-200">
            GCS não configurado. Arquivos salvos no servidor local.
          </div>
        </CardContent></Card>

        <Card><CardContent className="p-6 space-y-3">
          <div className="flex items-center gap-2"><Database className="h-5 w-5 text-blue-700" /><h3 className="font-display font-semibold">Banco de Dados</h3></div>
          <p className="text-xs text-slate-500">MongoDB operacional.</p>
          <div className="text-sm px-3 py-2 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-200">Conectado</div>
        </CardContent></Card>

        <Card><CardContent className="p-6 space-y-3">
          <div className="flex items-center gap-2"><Shield className="h-5 w-5 text-slate-700" /><h3 className="font-display font-semibold">Sessão</h3></div>
          <div className="text-xs text-slate-500">Usuário: <span className="font-medium text-slate-800">{user?.name}</span></div>
          <div className="text-xs text-slate-500">Papel: <span className="font-medium text-slate-800">{user?.role_label || user?.role}</span></div>
          <div className="text-xs text-slate-500">E-mail: <span className="font-medium text-slate-800">{user?.email}</span></div>
        </CardContent></Card>
      </div>

      {/* Zona de perigo (Resetar dados) removida a pedido do usuário */}
    </div>
  );
}

